/* Translations of a career map · mirrors backend/careermap/i18n.py.
 *
 * French is the canonical content. `data.i18n[locale]` is a full copy of the
 * map's text tree in another language (same shape), so edits and structural
 * changes line up path for path. An empty text, or one still equal to the
 * French, falls back to / counts as French. */

export const CONTENT_LOCALES = ['fr', 'nl', 'en'];

const TOP = ['title', 'subtitle', 'scale_note', 'floor_rule'];
const PARTS = {
  stages: ['key', 'question', 'title', 'summary'],
  levels: ['name', 'years'],
  rows: ['label'],
  rules: ['label', 'text'],
  scale: ['text'],
  competencies: ['name', 'text'],
};

export function textPaths(data) {
  const out = TOP.map(f => [[f], data[f] || '']);
  Object.entries(PARTS).forEach(([part, fields]) => {
    (data[part] || []).forEach((item, i) => fields.forEach(f => out.push([[part, i, f], item[f] || ''])));
  });
  (data.rows || []).forEach((row, ri) => (row.cells || []).forEach((c, ci) => out.push([['rows', ri, 'cells', ci, 'text'], c.text || ''])));
  const ev = data.evaluation || {};
  out.push([['evaluation', 'tip'], ev.tip || '']);
  (ev.groups || []).forEach((g, gi) => {
    out.push([['evaluation', 'groups', gi, 'title'], g.title || '']);
    (g.items || []).forEach((it, ii) => {
      out.push([['evaluation', 'groups', gi, 'items', ii, 'name'], it.name || '']);
      out.push([['evaluation', 'groups', gi, 'items', ii, 'hint'], it.hint || '']);
    });
  });
  return out;
}

const get = (tree, path) => path.reduce((t, p) => (t == null ? undefined : t[p]), tree);
const set = (tree, path, v) => { const last = path[path.length - 1]; get(tree, path.slice(0, -1))[last] = v; };
const strip = ({ i18n, promotion, ...rest }) => rest;  // eslint-disable-line no-unused-vars

/* The map as one language shows it: translated texts where there are any,
 * French elsewhere. Structure and thresholds always come from French. */
export function localize(data, locale) {
  if (!data || locale === 'fr') return data;
  const tree = data.i18n?.[locale];
  if (!tree) return data;
  const out = JSON.parse(JSON.stringify(data));
  textPaths(data).forEach(([path, fr]) => {
    const t = get(tree, path);
    set(out, path, typeof t === 'string' && t.trim() ? t : fr);
  });
  return out;
}

/* Make sure `draft.i18n[locale]` exists and matches the French structure,
 * pre-filled with whatever is translated and French elsewhere, so HR can
 * edit it in place. Mutates `draft`. */
export function ensureTree(draft, locale) {
  draft.i18n = draft.i18n || {};
  const old = draft.i18n[locale] || {};
  const tree = JSON.parse(JSON.stringify(strip(draft)));
  textPaths(draft).forEach(([path, fr]) => {
    const t = get(old, path);
    set(tree, path, typeof t === 'string' && t.trim() ? t : fr);
  });
  tree._same = old._same || [];
  draft.i18n[locale] = tree;
  return tree;
}

/* Share of non-empty French texts that have a translation in `locale`. */
export function coverage(data, locale) {
  const tree = data?.i18n?.[locale];
  const same = new Set(tree?._same || []);
  const paths = textPaths(data || {}).filter(([, fr]) => fr.trim());
  if (!paths.length) return 1;
  const done = paths.filter(([path, fr]) => {
    if (same.has(path.join('|'))) return true;
    const t = get(tree, path);
    return typeof t === 'string' && t.trim() && t.trim() !== fr.trim();
  }).length;
  return done / paths.length;
}
